import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useInFlight } from "../hooks/useInFlight";
import { Menu } from "./menu";
import DietaryModal from "./dietaryModal";
import MultiPersonNameInput from "./MultiPersonNameInput";
import DietaryRequirementsButton from "../shared/DietaryRequirementsButton";
import DietarySummary from "../shared/DietarySummary";
import { usePreorderContext } from "../context/PreorderContext";
import type { CustomerPreOrder } from "../../../common/jsonRpcTypes";
import type { MenuLimits } from "./preOrderUtils";

interface SelectOrderHeadingProps {
  readonly bookingTypeName: string;
  readonly description: string;
  readonly headingClassName: string;
}

function SelectOrderHeading({ bookingTypeName, description, headingClassName }: SelectOrderHeadingProps) {
  const { t } = useTranslation();
  return (
    <>
      <h2 className={headingClassName}>
        {t("linked_booking_section_title", "Select your {{bookingTypeName}} order", { bookingTypeName })}
      </h2>
      <div className="pre-order-info">
        <p className="gcw:pt-2 gcw:pb-4">{description}</p>
      </div>
    </>
  );
}

interface PreorderContentProps {
  readonly editNames: boolean;
  readonly addName: string;
  readonly addNameError: string;
  readonly namesExceedGuests?: boolean;
  readonly onMultipleNamesToggle: (checked: boolean) => void;
  readonly onAddName: () => void;
  readonly onAddNameChange: (name: string) => void;
  readonly onValidationChange: (isValid: boolean) => void;
  readonly onMenuLimitsChange: (limits: MenuLimits) => void;
  readonly externalValidationErrors: { [menuId: string]: string };
  readonly setCustomerPreOrder: React.Dispatch<React.SetStateAction<CustomerPreOrder | undefined>>;
  readonly headingSlot?: HTMLElement | null;
}

function PreorderContent({
  editNames,
  addName,
  addNameError,
  namesExceedGuests,
  onMultipleNamesToggle,
  onAddName,
  onAddNameChange,
  onValidationChange,
  onMenuLimitsChange,
  externalValidationErrors,
  setCustomerPreOrder,
  headingSlot,
}: PreorderContentProps) {
  const { t } = useTranslation();
  const {
    customerPreOrder,
    forNames,
    bookingData,
    multipleNames,
    linkedBookings,
    bookingTypeName,
    multiPersonOrderingEnabled,
  } = usePreorderContext();
  const [showDietaryModal, setShowDietaryModal] = useState(false);
  const dietarySave = useInFlight();
  const hasLinkedBookings = linkedBookings && linkedBookings.length > 0;

  const heading = (
    <h1
      id="preorder-heading"
      className={
        headingSlot
          ? "gcw:mt-0 gcw:mb-bs-3 gcw:text-2xl gcw:font-heading gcw:font-bold gcw:text-text-primary"
          : "payment-title"
      }
    >
      {t("pre_orders_title", "Pre-orders Menu")}
    </h1>
  );

  // Ordered list of booking type names guiding the guest through each section
  // of the linked-bookings flow: the main booking type first (when present),
  // then each linked booking type. Used to build the "Select your … order"
  // headings and the descriptions that point to the next section (or the
  // Place Order button on the final section).
  const sectionNames = hasLinkedBookings
    ? [...(bookingTypeName ? [bookingTypeName] : []), ...linkedBookings.map((linked) => linked.bookingTypeName)]
    : [];
  // Section index of the first linked booking (0 when there is no main section).
  const linkedSectionOffset = bookingTypeName ? 1 : 0;

  const getSectionDescription = (index: number): string => {
    const name = sectionNames[index];
    const isLast = index === sectionNames.length - 1;
    if (isLast) {
      return t(
        "linked_booking_section_description_last",
        "Select your {{bookingTypeName}} items which will be added to your basket. When you are happy with your basket click the {{placeOrderLabel}} button to confirm your order.",
        { bookingTypeName: name, placeOrderLabel: t("place_order_button", "Place your order") },
      );
    }
    return t(
      "linked_booking_section_description",
      "Select your {{bookingTypeName}} items which will be added to your basket and then select {{nextBookingTypeName}} items",
      { bookingTypeName: name, nextBookingTypeName: sectionNames[index + 1] },
    );
  };

  return (
    <div id="gc-preorder-content" className="app-main full-height-mobile">
      <div className="gcw:mb-2">
        {headingSlot ? (
          createPortal(heading, headingSlot)
        ) : (
          <div className="gcw:flex gcw:justify-between gcw:items-center">{heading}</div>
        )}
        <DietarySummary dietaryRequirements={customerPreOrder?.dietaryRequirements} forNames={forNames} />
        <div className="pre-order-info">
          <p>{t("pre_order_offers_info", "Take advantage of our pre-order offers!")}</p>
        </div>
      </div>
      {multiPersonOrderingEnabled && (
        <MultiPersonNameInput
          multipleNames={multipleNames}
          editNames={editNames}
          addName={addName}
          addNameError={addNameError}
          namesExceedGuests={namesExceedGuests}
          onToggle={onMultipleNamesToggle}
          onAddName={onAddName}
          onAddNameChange={onAddNameChange}
        />
      )}
      {!bookingData.venue.hideDietaryRequirements && (
        <div className="gcw:mb-bs-3">
          <div>
            <DietaryRequirementsButton
              dietaryCount={(customerPreOrder?.dietaryRequirements ?? []).reduce(
                (total, req) => total + (req.diet_types?.length ?? 0) + (req.allergies?.length ?? 0),
                0,
              )}
              onClick={(e) => {
                e.preventDefault();
                setShowDietaryModal(true);
              }}
            />
          </div>
        </div>
      )}
      {showDietaryModal && (
        <DietaryModal
          show={showDietaryModal}
          onClose={() => setShowDietaryModal(false)}
          forNames={forNames}
          customerPreOrder={customerPreOrder}
          inFlight={dietarySave}
          updateDietaryRequirements={(dietaryRequirements) => {
            setCustomerPreOrder((prev) => (prev ? { ...prev, dietaryRequirements } : prev));
          }}
        />
      )}
      <div id="gc-preorder-menu-form" className="pre-order-form">
        <div id="gc-preorder-menu-list">
          {bookingTypeName &&
            (hasLinkedBookings ? (
              <SelectOrderHeading
                bookingTypeName={bookingTypeName}
                description={getSectionDescription(0)}
                headingClassName="payment-title gcw:mt-4"
              />
            ) : (
              <h2 className="payment-title gcw:mt-4">{bookingTypeName}</h2>
            ))}
          {customerPreOrder && (
            <Menu
              onValidationChange={onValidationChange}
              onMenuLimitsChange={onMenuLimitsChange}
              externalValidationErrors={externalValidationErrors}
            />
          )}

          {hasLinkedBookings &&
            customerPreOrder &&
            linkedBookings?.map((linkedBooking, idx) => (
              <div key={linkedBooking.bookingTypeId}>
                <SelectOrderHeading
                  bookingTypeName={linkedBooking.bookingTypeName}
                  description={getSectionDescription(linkedSectionOffset + idx)}
                  headingClassName="payment-title gcw:mt-6"
                />
                {linkedBooking.menus && linkedBooking.menus.length > 0 ? (
                  <Menu
                    onValidationChange={onValidationChange}
                    onMenuLimitsChange={onMenuLimitsChange}
                    externalValidationErrors={externalValidationErrors}
                    isLinkedBooking={true}
                    linkedBookings={[linkedBooking]}
                  />
                ) : (
                  <p className="gcw:text-gray-500 gcw:italic">
                    {t("linked_booking_no_menus", "No menus available for this booking type")}
                  </p>
                )}
              </div>
            ))}
        </div>
      </div>
      <span className="kcal">{t("pre_order_kcal_info", "Adults require around 2000 kcal a day")}</span>
    </div>
  );
}

export { PreorderContent };
